import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import { SkuLocationImportResult, SkuLocationItem } from 'returnCenter-api';
import services from 'services';
import type { RootState, AppDispatch } from 'store';
import axios from 'axios';

type AdminThunkConf = {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: SkuLocationImportResult | { message: string } | undefined;
};

type InitialState = {
    // Upload state
    uploadLoading: boolean;
    uploadResult: SkuLocationImportResult | null;
    uploadError: string | null;

    // SKU locations list state
    skuLocations: SkuLocationItem[];
    skuLocationsLoading: boolean;
    skuLocationsError: string | null;
};

const initialState: InitialState = {
    uploadLoading: false,
    uploadResult: null,
    uploadError: null,

    skuLocations: [],
    skuLocationsLoading: false,
    skuLocationsError: null,
};

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clearUploadResult: (state) => {
            state.uploadResult = null;
            state.uploadError = null;
        },
    },
    extraReducers: (builder) => {
        // Upload SKU locations
        builder.addCase(uploadSkuLocations.pending, (state) => {
            state.uploadLoading = true;
            state.uploadResult = null;
            state.uploadError = null;
        });
        builder.addCase(uploadSkuLocations.fulfilled, (state, action: PayloadAction<SkuLocationImportResult>) => {
            state.uploadLoading = false;
            state.uploadResult = action.payload;
        });
        builder.addCase(uploadSkuLocations.rejected, (state, action) => {
            state.uploadLoading = false;
            if (action.payload === undefined) {
                // Cancelled by user - don't show error
                return;
            }
            if (action.payload && 'success' in action.payload) {
                // Backend returned a structured SkuLocationImportResult (e.g. 400/500)
                state.uploadResult = action.payload as SkuLocationImportResult;
            } else {
                state.uploadError = (action.payload as { message: string })?.message ?? 'Upload failed';
            }
        });

        // Get SKU locations
        builder.addCase(getSkuLocations.pending, (state) => {
            state.skuLocationsLoading = true;
            state.skuLocationsError = null;
        });
        builder.addCase(getSkuLocations.fulfilled, (state, action: PayloadAction<SkuLocationItem[]>) => {
            state.skuLocationsLoading = false;
            state.skuLocations = action.payload;
        });
        builder.addCase(getSkuLocations.rejected, (state, action) => {
            state.skuLocationsLoading = false;
            state.skuLocationsError = action.payload?.message ?? 'Failed to load SKU locations';
        });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

const uploadSkuLocations = createAsyncThunk<SkuLocationImportResult, File, AdminThunkConf>(
    'admin/uploadSkuLocations',
    async (file, { rejectWithValue, signal }) => {
        try {
            const response = await services.skuLocation.uploadSkuLocations(file, signal);
            return response;
        } catch (error) {
            if (axios.isCancel(error) || signal.aborted) {
                return rejectWithValue(undefined);
            }
            if (axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object' && 'success' in error.response.data) {
                return rejectWithValue(error.response.data as SkuLocationImportResult);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

const getSkuLocations = createAsyncThunk<SkuLocationItem[], void, AdminThunkConf>(
    'admin/getSkuLocations',
    async (_, { rejectWithValue }) => {
        try {
            const response = await services.skuLocation.getSkuLocations();
            return response;
        } catch (error) {
            if (axios.isCancel(error)) {
                return rejectWithValue(undefined);
            }
            return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
        }
    },
);

// Selectors
const selectUploadLoading = (state: RootState) => state.admin.uploadLoading;
const selectUploadResult = (state: RootState) => state.admin.uploadResult;
const selectUploadError = (state: RootState) => state.admin.uploadError;
const selectSkuLocations = (state: RootState) => state.admin.skuLocations;
const selectSkuLocationsLoading = (state: RootState) => state.admin.skuLocationsLoading;
const selectSkuLocationsError = (state: RootState) => state.admin.skuLocationsError;

export const adminThunks = {
    uploadSkuLocations,
    getSkuLocations,
};

export const adminSelectors = {
    selectUploadLoading,
    selectUploadResult,
    selectUploadError,
    selectSkuLocations,
    selectSkuLocationsLoading,
    selectSkuLocationsError,
};

export const { clearUploadResult } = adminSlice.actions;

export default adminSlice.reducer;
